import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  getCategoryColors
} from '@bliks-io/ui';
import { truncateText } from '@/lib';
import type { DashboardProcess } from '@bliks-io/contracts';

const CATEGORY_LEGEND = [
  { key: 'core', labelKey: 'processMap.lanes.core', color: getCategoryColors('core').stroke },
  {
    key: 'management',
    labelKey: 'processMap.lanes.management',
    color: getCategoryColors('management').stroke
  },
  {
    key: 'support',
    labelKey: 'processMap.lanes.support',
    color: getCategoryColors('support').stroke
  },
  {
    key: 'uncategorized',
    labelKey: 'processMap.lanes.uncategorized',
    color: getCategoryColors('uncategorized').stroke
  }
] as const;

interface DashboardCapacityChartProps {
  processes: DashboardProcess[];
}

export function DashboardCapacityChart({ processes }: DashboardCapacityChartProps) {
  const { t } = useTranslation();

  const chartData = processes
    .filter((p) => p.capacityBinding)
    .map((p) => ({
      name: truncateText(p.name, 20),
      hours: p.capacityBinding!.hoursPerMonth,
      fill: getCategoryColors(p.category).stroke
    }))
    .sort((a, b) => b.hours - a.hours);

  if (chartData.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">{t('dashboard.capacity.title')}</CardTitle>
          <CardDescription>{t('dashboard.capacity.subtitle')}</CardDescription>
        </div>
        <div className="flex flex-wrap gap-3">
          {CATEGORY_LEGEND.map((cat) => (
            <div key={cat.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="inline-block h-3 w-3 rounded-xs"
                style={{ backgroundColor: cat.color }}
              />
              {t(cat.labelKey)}
            </div>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          {/* top margin gives the topmost Y tick label headroom so it is not clipped */}
          <BarChart data={chartData} margin={{ top: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            {/* width="auto" sizes the axis to the labels (no left clipping on 4-digit values);
                interval={0} renders every tick at its exact scale coordinate instead of
                recharts' DOM-measurement path, which nudges boundary labels off their gridlines */}
            <YAxis width="auto" interval={0} tickFormatter={(value: number) => `${value}h`} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const { name, hours } = payload[0].payload;
                return (
                  <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
                    <p className="font-medium">{name}</p>
                    <p className="text-muted-foreground">
                      {t('dashboard.hoursSuffix', { value: hours.toFixed(1) })}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
